import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminFormPage } from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { ResourceForm } from "../ResourceForm/ResourceForm";
import { useResourceMutations, useResourceUdfDefinitions } from "../api";
import { emptyResourceForm } from "../types";
import type { ResourceFormValues } from "../types";

const FORM_ID = "resource-form";

export const New = (): JSX.Element => {
  const navigate = useNavigate();
  const { create } = useResourceMutations();
  const { definitions, isLoading } = useResourceUdfDefinitions();
  const [submitting, setSubmitting] = useState(false);
  // Remounting the form via key resets it to empty after "Save and Add Another".
  const [formKey, setFormKey] = useState(0);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);
  // The native Save button submits directly (nextAction stays "edit"); "Save and
  // Add Another" sets "new" then programmatically submits. handleSubmit resets it
  // to "edit" so a later plain Save navigates as expected.
  const nextAction = useRef<"edit" | "new">("edit");

  const handleSubmit = async (values: ResourceFormValues) => {
    setSubmitting(true);
    const addAnother = nextAction.current === "new";
    try {
      const result: any = await create(values);
      const newId = result?.data?.id;
      if (addAnother) {
        setFormKey((k) => k + 1);
        setToast({ variant: "success", text: `'${values.name}' saved` });
        setSubmitting(false);
      } else {
        navigate(newId ? `../${newId}/edit` : "..", {
          relative: "path",
          state: {
            toast: { variant: "success", text: `'${values.name}' saved` },
          },
        });
      }
    } catch (err) {
      console.error("Failed to create resource", err);
      setToast({ variant: "danger", text: "Could not save resource." });
      setSubmitting(false);
    } finally {
      nextAction.current = "edit";
    }
  };

  const triggerAddAnother = () => {
    nextAction.current = "new";
    (
      document.getElementById(FORM_ID) as HTMLFormElement | null
    )?.requestSubmit();
  };

  if (isLoading) return <ScopeStackSpinner />;

  return (
    <>
      <AdminFormPage
        title="Add a Resource"
        formId={FORM_ID}
        onCancel={() => navigate("..")}
        onSaveAndAddAnother={triggerAddAnother}
        isSubmitting={submitting}
      >
        <ResourceForm
          key={formKey}
          id={FORM_ID}
          initialValues={{
            ...emptyResourceForm,
            userDefinedFields: definitions,
          }}
          isSubmitting={submitting}
          onSubmit={handleSubmit}
        />
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default New;
